/**
 * Validation Schemas (Zod)
 *
 * Runtime validation schemas for Trip and Activity entities.
 * These schemas provide both runtime validation and TypeScript type inference.
 *
 * See data-model.md for full validation rules.
 */

import { z } from 'zod';

// ============================================================================
// Trip Validation Schemas
// ============================================================================

/**
 * Base Trip object schema without refinements (allows .omit() / .partial())
 */
const tripBaseSchema = z.object({
  id: z.string().uuid('Invalid trip ID format'),
  name: z
    .string()
    .trim()
    .min(1, 'Trip name is required')
    .max(200, 'Trip name must be 200 characters or less'),
  startDate: z.coerce.date({ message: 'Start date is required' }),
  endDate: z.coerce.date({ message: 'End date is required' }),
  tags: z
    .array(
      z
        .string()
        .trim()
        .min(1, 'Tag cannot be empty')
        .max(30, 'Tag must be 30 characters or less')
    )
    .max(20, 'Maximum 20 tags allowed')
    .default([]),
  createdAt: z.date(),
  updatedAt: z.date(),
  userId: z.string().min(1, 'User ID is required'),
});

/**
 * Full Trip schema (stored entity)
 */
export const tripSchema = tripBaseSchema.refine(
  (data) => data.endDate >= data.startDate,
  { message: 'End date must be on or after start date', path: ['endDate'] }
);

/**
 * Schema for creating a new trip (omits auto-generated fields)
 */
export const createTripSchema = tripBaseSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    userId: true, // Set automatically by service
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: 'End date must be on or after start date',
    path: ['endDate'],
  });

/**
 * Schema for updating an existing trip (all fields optional)
 */
export const updateTripSchema = tripBaseSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    userId: true,
  })
  .partial()
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return data.endDate >= data.startDate;
      }
      return true;
    },
    {
      message: 'End date must be on or after start date',
      path: ['endDate'],
    }
  );

// ============================================================================
// Activity Validation Schemas
// ============================================================================

/**
 * Full Activity schema (stored entity)
 */
export const activitySchema = z.object({
  id: z.string().uuid('Invalid activity ID format'),
  tripId: z.string().uuid('Invalid trip ID format'),
  name: z
    .string()
    .trim()
    .min(1, 'Activity name is required')
    .max(200, 'Activity name must be 200 characters or less'),
  location: z
    .string()
    .trim()
    .min(1, 'Location is required')
    .max(200, 'Location must be 200 characters or less'),
  dayIndex: z
    .number()
    .int('Day index must be an integer')
    .min(0, 'Day index cannot be negative')
    .nullable(),
  order: z
    .number()
    .int('Order must be an integer')
    .min(0, 'Order cannot be negative')
    .nullable(),
  scheduledTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Time must be in HH:mm format (00:00-23:59)')
    .nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Schema for creating a new activity (omits auto-generated fields)
 */
export const createActivitySchema = activitySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  order: true, // Computed automatically by service
});

/**
 * Schema for updating an existing activity (all fields optional)
 */
export const updateActivitySchema = createActivitySchema.partial();

/**
 * Schema for reordering an activity within the same day
 */
export const reorderActivitySchema = z.object({
  activityId: z.string().uuid('Invalid activity ID format'),
  newOrder: z
    .number()
    .int('Order must be an integer')
    .min(0, 'Order cannot be negative'),
});

/**
 * Schema for moving an activity to a different day
 */
export const moveToDaySchema = z.object({
  activityId: z.string().uuid('Invalid activity ID format'),
  targetDayIndex: z
    .number()
    .int('Day index must be an integer')
    .min(0, 'Day index cannot be negative'),
});

// ============================================================================
// Type Inference
// ============================================================================

/**
 * Infer TypeScript types from Zod schemas
 * These can be used in service layer and components
 */
export type TripSchemaType = z.infer<typeof tripSchema>;
export type CreateTripSchemaType = z.infer<typeof createTripSchema>;
export type UpdateTripSchemaType = z.infer<typeof updateTripSchema>;

export type ActivitySchemaType = z.infer<typeof activitySchema>;
export type CreateActivitySchemaType = z.infer<typeof createActivitySchema>;
export type UpdateActivitySchemaType = z.infer<typeof updateActivitySchema>;
export type ReorderActivitySchemaType = z.infer<typeof reorderActivitySchema>;
export type MoveToDaySchemaType = z.infer<typeof moveToDaySchema>;

// ============================================================================
// Day Index and Time Validation (Feature 003)
// ============================================================================

/**
 * Time format regex for HH:mm validation (24-hour format)
 * Hours: 00-23, Minutes: 00-59
 */
const TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Validate if a day index is within valid range for a trip
 * @param dayIndex - Day index to validate (0-based)
 * @param tripDurationDays - Total number of days in the trip
 * @returns true if dayIndex is valid (>= 0 and < tripDurationDays)
 */
export function validateDayIndex(dayIndex: number, tripDurationDays: number): boolean {
  return Number.isInteger(dayIndex) && dayIndex >= 0 && dayIndex < tripDurationDays;
}

/**
 * Validate if a time string is in valid HH:mm format
 * @param time - Time string to validate
 * @returns true if time matches HH:mm format (24-hour)
 */
export function validateScheduledTime(time: string): boolean {
  return TIME_REGEX.test(time);
}

/**
 * Error messages for day index and time validation
 */
export const DAY_INDEX_ERROR_MESSAGES = {
  OUT_OF_RANGE: '所選日期超出旅程範圍,請先調整旅程天數',
  INVALID_FORMAT: '日期索引格式錯誤,必須是非負整數',
} as const;

export const TIME_ERROR_MESSAGES = {
  INVALID_FORMAT: '時間格式錯誤,請使用 HH:mm 格式 (例如: 09:00, 14:30)',
  INVALID_HOUR: '小時必須在 00-23 之間',
  INVALID_MINUTE: '分鐘必須在 00-59 之間',
} as const;

// ============================================================================
// Validation Helper Functions
// ============================================================================

/**
 * Validate and parse trip creation input
 * @throws ZodError if validation fails
 */
export function validateCreateTrip(input: unknown): CreateTripSchemaType {
  return createTripSchema.parse(input);
}

/**
 * Validate and parse trip update input
 * @throws ZodError if validation fails
 */
export function validateUpdateTrip(input: unknown): UpdateTripSchemaType {
  return updateTripSchema.parse(input);
}

/**
 * Validate and parse activity creation input
 * @throws ZodError if validation fails
 */
export function validateCreateActivity(input: unknown): CreateActivitySchemaType {
  return createActivitySchema.parse(input);
}

/**
 * Validate and parse activity update input
 * @throws ZodError if validation fails
 */
export function validateUpdateActivity(input: unknown): UpdateActivitySchemaType {
  return updateActivitySchema.parse(input);
}

/**
 * Validate and parse reorder activity input
 * @throws ZodError if validation fails
 */
export function validateReorderActivity(input: unknown): ReorderActivitySchemaType {
  return reorderActivitySchema.parse(input);
}

/**
 * Validate and parse move-to-day input
 * @throws ZodError if validation fails
 */
export function validateMoveToDay(input: unknown): MoveToDaySchemaType {
  return moveToDaySchema.parse(input);
}

/**
 * Safe parse with error handling (returns { success: true, data } or { success: false, error })
 */
export function safeParseCreateTrip(input: unknown) {
  return createTripSchema.safeParse(input);
}

export function safeParseUpdateTrip(input: unknown) {
  return updateTripSchema.safeParse(input);
}

export function safeParseCreateActivity(input: unknown) {
  return createActivitySchema.safeParse(input);
}

export function safeParseUpdateActivity(input: unknown) {
  return updateActivitySchema.safeParse(input);
}

export function safeParseReorderActivity(input: unknown) {
  return reorderActivitySchema.safeParse(input);
}

export function safeParseMoveToDay(input: unknown) {
  return moveToDaySchema.safeParse(input);
}

// ============================================================================
// Error Formatting Helpers
// ============================================================================

/**
 * Format Zod validation errors into user-friendly messages
 */
export function formatValidationErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};

  error.issues.forEach((err: z.ZodIssue) => {
    const path = err.path.join('.');
    errors[path] = err.message;
  });

  return errors;
}

/**
 * Get first validation error message (for simple error display)
 */
export function getFirstValidationError(error: z.ZodError): string {
  return error.issues[0]?.message || 'Validation failed';
}
